/**
 * MirageSkillEffect — Specter veil-path evolution: replaces Veil.
 *
 * A deepened perception field. Where Veil interferes with senses at 20% over
 * 4 tiles, Mirage corrupts them at 40% over 6 tiles and slows how quickly
 * enemies build aggro while inside the field (15% rate reduction).
 *
 * Extends VeilSkillEffect — inherits the same per-frame scan, debuff
 * apply/remove, Afterburn, Gaslight, and White Noise hooks. Overrides
 * only the base parameters:
 *   - basePerceptionReduction: 0.4 (vs. Veil's 0.2)
 *   - baseRadiusTiles: 6 (vs. Veil's 4)
 *   - Adds aggroGainMultiplier reduction: 0.15 per enemy in field
 *
 * Deep Veil and Afterburn upgrades apply on top of Mirage's deepened base
 * via the resolver scope 'veil' (enhancesId chain in the JSON).
 *
 * Resolver queries use scope 'veil' for upgrade keys (inherited from
 * VeilSkillEffect) since MirageSkillEffect inherits all veil upgrade hooks.
 *
 * The `aggroGainMultiplier` debuff is applied per-enemy each frame alongside
 * the veil_debuff. It is removed on the same cadence.
 *
 * Implements SkillEffect via ToggleSkillEffect (through VeilSkillEffect).
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Mirage
 */

import { VeilSkillEffect, type VeilDeps } from './VeilSkillEffect.js';

// ---------------------------------------------------------------------------
// Config override
// ---------------------------------------------------------------------------

import type { VeilConfig } from './VeilSkillEffect.js';

const MIRAGE_BASE_CONFIG: Partial<VeilConfig> = {
  baseRadiusTiles: 6,               // 6-tile base radius (vs. Veil's 4)
  basePerceptionReduction: 0.4,     // 40% base reduction (vs. Veil's 20%)
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class MirageSkillEffect extends VeilSkillEffect {
  override readonly skillId: string = 'mirage';

  // Aggro reduction (15% base, i.e. aggroGainMultiplier = 0.85 on each enemy)
  protected readonly baseAggroGainReduction = 0.15;
  private _aggroDebuffedEnemyIds: Set<string> = new Set();

  constructor(deps: VeilDeps, config: Partial<VeilConfig> = {}) {
    super(deps, { ...MIRAGE_BASE_CONFIG, ...config });
  }

  // Observable state
  get aggroDebuffedEnemyIds(): ReadonlySet<string> { return this._aggroDebuffedEnemyIds; }

  override activate(): boolean {
    this._aggroDebuffedEnemyIds.clear();
    return super.activate();
  }

  protected override onDeactivate(): void {
    // Remove aggro debuffs from all currently-affected enemies before VeilSkillEffect cleanup
    for (const enemyId of this._aggroDebuffedEnemyIds) {
      this.deps.statusEffects.remove('mirage_aggro_debuff', enemyId);
    }
    this._aggroDebuffedEnemyIds.clear();
    super.onDeactivate();
  }

  protected override updateWhileActive(delta: number): void {
    super.updateWhileActive(delta);

    // Apply / maintain aggro-gain reduction on enemies in the field
    // VeilSkillEffect.updateWhileActive already updated _debuffedEnemyIds
    const aggroReduction = this._resolveAggroGainReduction();

    // Enemies that left — remove aggro debuff
    for (const enemyId of this._aggroDebuffedEnemyIds) {
      if (!this._debuffedEnemyIds.has(enemyId)) {
        this.deps.statusEffects.remove('mirage_aggro_debuff', enemyId);
        this._aggroDebuffedEnemyIds.delete(enemyId);
      }
    }

    // Enemies in field — apply aggro debuff if not already applied
    for (const enemyId of this._debuffedEnemyIds) {
      if (!this._aggroDebuffedEnemyIds.has(enemyId)) {
        // STUB: deferred — no aggro system reads this multiplier
        this.deps.statusEffects.apply('mirage_aggro_debuff', {
          sourceSkillId: 'mirage',
          durationMs: -1,
          targetEntityId: enemyId,
          aggroGainMultiplier: 1 - aggroReduction,
        });
        this._aggroDebuffedEnemyIds.add(enemyId);
      }
    }
  }

  private _resolveAggroGainReduction(): number {
    const bonus = this.deps.resolver.getAdditiveEffect('mirage', 'aggroGainMultiplier');
    return this.baseAggroGainReduction + bonus;
  }
}
